import { Center, Loader, Text } from '@mantine/core';
import { ClerkProvider, useAuth, useClerk } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { api, setAuthTokenGetter, type AuthConfig } from '../lib/api.js';

/**
 * Decides which sign-in mechanism the app uses, at runtime.
 *
 * The API tells us. That keeps the publishable key out of the bundle, so
 * rotating it or switching Clerk off takes effect on reload rather than
 * needing a rebuild — and it means a deploy of this code with Clerk
 * unconfigured simply carries on using the password login.
 */

/**
 * What the rest of the app may do about auth, without knowing which provider
 * is in play.
 *
 * This exists because hooks cannot be conditional. Calling useClerk() directly
 * in a component that also renders without ClerkProvider throws and takes the
 * whole page down — which is exactly what happened to /app before this.
 */
interface AuthActions {
  provider: 'clerk' | 'password';
  /** Ends the provider's own session, if it has one. Never throws. */
  signOutProvider: () => Promise<void>;
}

const AuthActionsContext = createContext<AuthActions>({
  provider: 'password',
  signOutProvider: async () => {},
});

export function useAuthActions(): AuthActions {
  return useContext(AuthActionsContext);
}

/** Feeds Clerk's token to the plain API client. */
function ClerkTokenBridge({ children }: { children: ReactNode }) {
  const { getToken, isLoaded } = useAuth();
  const clerk = useClerk();

  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    // Cleared on unmount so a stale getter cannot outlive the provider.
    return () => setAuthTokenGetter(null);
  }, [getToken]);

  // This component only ever renders inside ClerkProvider, so useClerk here is
  // safe in a way it is not anywhere else.
  const actions = useMemo<AuthActions>(
    () => ({
      provider: 'clerk',
      signOutProvider: async () => {
        await clerk.signOut().catch(() => {});
      },
    }),
    [clerk],
  );

  if (!isLoaded) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }
  return <AuthActionsContext.Provider value={actions}>{children}</AuthActionsContext.Provider>;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const cfg = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<AuthConfig>('/admin/auth-config'),
    // Which mechanism is in use does not change during a session.
    staleTime: Infinity,
    retry: 1,
  });

  if (cfg.isLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  // If the config call fails we fall through to the password login rather than
  // blocking the app. An unreachable API is a problem the login page can show.
  if (cfg.data?.provider !== 'clerk' || !cfg.data.publishableKey) {
    return <>{children}</>;
  }

  return (
    <ClerkProvider
      publishableKey={cfg.data.publishableKey}
      // Clerk's own routing would fight React Router; we mount its components
      // ourselves on our own routes instead.
      afterSignOutUrl="/"
    >
      <ClerkTokenBridge>{children}</ClerkTokenBridge>
    </ClerkProvider>
  );
}

/** Whether Clerk is in use, for components that need to branch. */
export function useAuthConfig(): AuthConfig | undefined {
  return useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<AuthConfig>('/admin/auth-config'),
    staleTime: Infinity,
  }).data;
}

export function AuthError({ message }: { message: string }) {
  return (
    <Center mih="100vh">
      <Text c="red">{message}</Text>
    </Center>
  );
}
